import React from "react";
import { ChevronRight, ShieldCheck, MapPin, LineChart, Landmark, FileCheck, CheckCircle2 } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import NewsletterForm from "@/components/site/NewsletterForm";
import PeruRibbon from "@/components/site/PeruRibbon";

const VALUE_IMAGES = ["/images/financial-planning.jpg", "/images/consultant.jpg", "/images/entrepreneur-woman.jpg"];

export default function HomePage() {
  const { t, tArray, localePath } = useI18n();

  const valueItems = tArray("value_props.items");
  const bullets = tArray("official_platform.bullets");
  const steps = tArray("how_it_works.steps");
  const advisors = tArray("advisors.list");
  const faqItems = tArray("faq.items");
  const trustItems = tArray("trust_bar.items");

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section
        className="relative bg-[#0E2A47] text-white overflow-hidden"
        data-testid="hero-section"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24 grid gap-12 md:grid-cols-12 items-center">
          <div className="md:col-span-7">
            <span
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#3FB68A]"
              data-testid="hero-eyebrow"
            >
              <span className="inline-block w-6 h-px bg-[#3FB68A]" />
              {t("hero.eyebrow")}
            </span>
            <h1
              className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
              data-testid="hero-title"
            >
              {t("hero.title_line1")}{" "}
              <span className="text-[#3FB68A]">{t("hero.title_line2")}</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-white/80 max-w-2xl leading-relaxed" data-testid="hero-subtitle">
              {t("hero.subtitle")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#newsletter"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0F5F3F] text-white font-medium text-sm hover:bg-[#0a4c32]"
                data-testid="hero-cta-primary"
              >
                {t("hero.cta_primary")}
                <ChevronRight className="w-4 h-4" aria-hidden="true" />
              </a>
              <a
                href="#programa"
                className="inline-flex items-center px-6 py-3 rounded-full border border-white/25 text-white text-sm hover:bg-white/5"
                data-testid="hero-cta-secondary"
              >
                {t("hero.cta_secondary")}
              </a>
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10">
              <img
                src="/images/hero-business.jpg"
                alt={t("hero.image_alt")}
                className="w-full h-[420px] object-cover"
                loading="eager"
                data-testid="hero-image"
              />
              <div className="absolute left-4 top-4 flex items-center gap-2 bg-white/95 text-[#0E2A47] rounded-full px-3 py-1 text-xs font-medium">
                <MapPin className="w-3.5 h-3.5" aria-hidden="true" />
                San Isidro · Lima · Perú
              </div>
            </div>
          </div>
        </div>
        <PeruRibbon height={4} />
      </section>

      {/* Trust bar */}
      <section className="bg-[#F5F6F8] border-y border-[#0E2A47]/5" data-testid="trust-bar">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-8 grid gap-6 md:grid-cols-12 items-center">
          <div className="md:col-span-4 text-sm uppercase tracking-[0.18em] text-[#0E2A47]/60">
            {t("trust_bar.title")}
          </div>
          <ul className="md:col-span-8 grid gap-3 sm:grid-cols-2">
            {trustItems.map((item, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-[#0E2A47]">
                <ShieldCheck className="w-4 h-4 mt-0.5 text-[#0F5F3F] shrink-0" aria-hidden="true" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Value propositions */}
      <section id="programa" className="py-20 md:py-28" data-testid="value-props-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="max-w-3xl">
            <span className="text-xs uppercase tracking-[0.22em] text-[#0F5F3F]" data-testid="value-props-eyebrow">
              {t("value_props.eyebrow")}
            </span>
            <h2 className="mt-4 font-serif text-3xl md:text-4xl lg:text-5xl text-[#0E2A47] leading-tight" data-testid="value-props-title">
              {t("value_props.title")}
            </h2>
            <p className="mt-5 text-[#0E2A47]/75 text-base md:text-lg">{t("value_props.subtitle")}</p>
          </div>
          <div className="mt-14 grid gap-8 md:grid-cols-3">
            {valueItems.map((item, idx) => (
              <article
                key={idx}
                className="rounded-2xl overflow-hidden border border-[#0E2A47]/10 bg-white"
                data-testid={`value-card-${idx}`}
              >
                <img
                  src={VALUE_IMAGES[idx] || VALUE_IMAGES[0]}
                  alt={item.image_alt}
                  className="w-full h-56 object-cover"
                  loading="lazy"
                />
                <div className="p-6">
                  <h3 className="font-serif text-xl text-[#0E2A47] leading-snug">{item.title}</h3>
                  <p className="mt-3 text-sm text-[#0E2A47]/70 leading-relaxed">{item.description}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Official platform strip */}
      <section className="bg-[#0E2A47] text-white py-20 md:py-24" data-testid="official-platform-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid gap-12 md:grid-cols-12 items-center">
          <div className="md:col-span-5 order-2 md:order-1">
            <img
              src="/images/meeting.jpg"
              alt={t("official_platform.image_alt")}
              className="w-full h-[420px] object-cover rounded-2xl border border-white/10"
              loading="lazy"
            />
          </div>
          <div className="md:col-span-7 order-1 md:order-2">
            <span className="text-xs uppercase tracking-[0.22em] text-[#3FB68A]">
              {t("official_platform.eyebrow")}
            </span>
            <h2 className="mt-4 font-serif text-3xl md:text-4xl lg:text-5xl leading-tight" data-testid="official-platform-title">
              {t("official_platform.title")}
            </h2>
            <p className="mt-5 text-white/80 text-base md:text-lg">{t("official_platform.description")}</p>
            <ul className="mt-6 grid gap-3">
              {bullets.map((b, idx) => (
                <li key={idx} className="flex items-start gap-3 text-sm md:text-base text-white/85">
                  <CheckCircle2 className="w-5 h-5 mt-0.5 text-[#3FB68A] shrink-0" aria-hidden="true" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <a
              href="#newsletter"
              className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#3FB68A] text-[#0E2A47] font-semibold text-sm hover:bg-[#2fa578]"
              data-testid="official-platform-cta"
            >
              {t("official_platform.cta")}
              <ChevronRight className="w-4 h-4" aria-hidden="true" />
            </a>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="como-funciona" className="py-20 md:py-28 bg-white" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div className="max-w-2xl">
              <span className="text-xs uppercase tracking-[0.22em] text-[#0F5F3F]">
                {t("how_it_works.eyebrow")}
              </span>
              <h2 className="mt-3 font-serif text-3xl md:text-4xl lg:text-5xl text-[#0E2A47] leading-tight">
                {t("how_it_works.title")}
              </h2>
            </div>
            <a
              href="#newsletter"
              className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#0E2A47]/20 text-[#0E2A47] text-sm hover:bg-[#0E2A47]/5"
              data-testid="how-works-cta"
            >
              {t("nav.newsletter")}
              <ChevronRight className="w-4 h-4" aria-hidden="true" />
            </a>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {steps.map((s, idx) => {
              const Icon = [FileCheck, LineChart, Landmark][idx] || FileCheck;
              return (
                <div
                  key={idx}
                  className="p-8 rounded-2xl border border-[#0E2A47]/10 bg-[#F5F6F8]"
                  data-testid={`how-step-${idx}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-serif text-3xl text-[#C8102E]">{s.number}</span>
                    <Icon className="w-6 h-6 text-[#0E2A47]" aria-hidden="true" />
                  </div>
                  <h3 className="mt-4 font-serif text-xl text-[#0E2A47]">{s.title}</h3>
                  <p className="mt-3 text-sm text-[#0E2A47]/75 leading-relaxed">{s.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Advisors */}
      <section id="especialistas" className="py-20 md:py-28 bg-[#F5F6F8]" data-testid="advisors-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="max-w-3xl">
            <span className="text-xs uppercase tracking-[0.22em] text-[#0F5F3F]">
              {t("advisors.eyebrow")}
            </span>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl lg:text-5xl text-[#0E2A47] leading-tight">
              {t("advisors.title")}
            </h2>
            <p className="mt-5 text-[#0E2A47]/75 text-base md:text-lg">{t("advisors.subtitle")}</p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {advisors.map((a, idx) => (
              <article key={idx} className="bg-white rounded-2xl border border-[#0E2A47]/10 p-8" data-testid={`advisor-${idx}`}>
                <div className="text-xs uppercase tracking-[0.22em] text-[#C8102E]">
                  0{idx + 1}
                </div>
                <h3 className="mt-4 font-serif text-2xl text-[#0E2A47]">{a.name}</h3>
                <div className="mt-2 text-sm text-[#0F5F3F] font-medium">{a.role}</div>
                <div className="mt-4 inline-flex items-center gap-1.5 text-xs text-[#0E2A47]/60">
                  <MapPin className="w-3.5 h-3.5" aria-hidden="true" />
                  {a.location}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-20 md:py-28 bg-white" data-testid="testimonial-section">
        <div className="max-w-6xl mx-auto px-5 md:px-8 grid gap-12 md:grid-cols-12 items-center">
          <div className="md:col-span-5">
            <img
              src="/images/savings.jpg"
              alt={t("testimonial.image_alt")}
              className="w-full h-[380px] object-cover rounded-2xl"
              loading="lazy"
            />
          </div>
          <div className="md:col-span-7">
            <span className="text-xs uppercase tracking-[0.22em] text-[#0F5F3F]">
              {t("testimonial.eyebrow")}
            </span>
            <blockquote className="mt-4 font-serif text-2xl md:text-3xl text-[#0E2A47] leading-snug" data-testid="testimonial-quote">
              &ldquo;{t("testimonial.quote")}&rdquo;
            </blockquote>
            <div className="mt-6 flex items-center gap-3">
              <div className="w-1 h-8 bg-[#C8102E]" aria-hidden="true" />
              <div>
                <div className="font-medium text-[#0E2A47]">{t("testimonial.author")}</div>
                <div className="text-sm text-[#0E2A47]/60">{t("testimonial.role")}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 md:py-28 bg-[#0E2A47] text-white" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-5 md:px-8">
          <span className="text-xs uppercase tracking-[0.22em] text-[#3FB68A]">
            {t("faq.eyebrow")}
          </span>
          <h2 className="mt-3 font-serif text-3xl md:text-4xl lg:text-5xl leading-tight" data-testid="faq-title">
            {t("faq.title")}
          </h2>
          <div className="mt-10 space-y-3">
            {faqItems.map((item, idx) => (
              <details
                key={idx}
                className="group rounded-xl border border-white/10 bg-white/5 open:bg-white/8 px-5 py-4"
                data-testid={`faq-item-${idx}`}
              >
                <summary className="cursor-pointer list-none flex items-start justify-between gap-4 text-base md:text-lg font-medium">
                  <span>{item.question}</span>
                  <span className="text-[#3FB68A] group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-sm md:text-base text-white/80 leading-relaxed">{item.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section id="newsletter" className="py-20 md:py-28 bg-[#F5F6F8]" data-testid="newsletter-section">
        <div className="max-w-6xl mx-auto px-5 md:px-8 grid gap-12 md:grid-cols-12 items-start">
          <div className="md:col-span-6">
            <span className="text-xs uppercase tracking-[0.22em] text-[#0F5F3F]">
              {t("newsletter.eyebrow")}
            </span>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl lg:text-5xl text-[#0E2A47] leading-tight" data-testid="newsletter-title">
              {t("newsletter.title")}
            </h2>
            <p className="mt-5 text-[#0E2A47]/75 text-base md:text-lg">{t("newsletter.subtitle")}</p>
            <div className="mt-8 rounded-2xl overflow-hidden">
              <img
                src="/images/lima-san-isidro.jpg"
                alt={t("hero.image_alt")}
                className="w-full h-64 object-cover"
                loading="lazy"
              />
            </div>
          </div>
          <div className="md:col-span-6">
            <div className="bg-white rounded-2xl p-8 border border-[#0E2A47]/10 shadow-sm">
              <NewsletterForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
